import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories, toggleCategoryStatus, fetchCategoryConfig } from "../../../redux/slice/categorySlice";
import { PlusIcon } from "../../../assets/icons";
import StatusChip from "../../../components/statusChip/StatusChip";
import CommonButton from "../../../components/commonButton";
import CategoryModal from "./CategoryModal";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import CustomSelect from "../../../components/form/CustomSelect";
import { usePermission } from "../../../hooks/usePermission";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";

const MODULE_SLUG = "interest_category";

const CategoryList = () => {
    const dispatch = useDispatch();
    const { list, pagination, isLoading, config } = useSelector((state) => state.category);
    const { hasPermission } = usePermission();

    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState({ value: 10, label: 10 });
    const [selectedType, setSelectedType] = useState({ value: "interest", label: "Interest" });

    const [showModal, setShowModal] = useState(false);
    const [isView, setIsView] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusItem, setStatusItem] = useState(null);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchData = useCallback(() => {
        dispatch(fetchCategories({
            page: currentPage,
            limit: limit.value,
            search: debouncedSearch,
            type: selectedType.value
        }));
    }, [dispatch, currentPage, limit.value, debouncedSearch, selectedType.value]);

    useEffect(() => {
        dispatch(fetchCategoryConfig());
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleEdit = (item) => {
        setEditItem(item);
        setIsView(false);
        setShowModal(true);
    };

    const handleView = (item) => {
        setEditItem(item);
        setIsView(true);
        setShowModal(true);
    };

    const handleConfirmStatusChange = async () => {
        if (!statusItem) return;
        try {
            const result = await dispatch(
                toggleCategoryStatus({
                    id: statusItem.id,
                    is_active: !statusItem.is_active,
                })
            ).unwrap();
            if (result.success) {
                fetchData();
            }
        } catch (error) {
            // Error handled by slice toast
        } finally {
            setShowStatusModal(false);
            setStatusItem(null);
        }
    };

    const columns = useMemo(() => [
        {
            id: "name",
            header: "Name",
            cell: (_, row) => row.name || "-"
        },
        {
            id: "type",
            header: "Type",
            cell: (_, row) => row.type || "-"
        },
        {
            id: "status",
            header: "Status",
            cell: (_, row) => (
                <StatusChip
                    bg={row.is_active ? "success" : "danger"}
                    pill
                    title={row.is_active ? "Active" : "Inactive"}
                    className={canUpdate ? "cursor-pointer" : ""}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canUpdate) {
                            setStatusItem(row);
                            setShowStatusModal(true);
                        }
                    }}
                />
            )
        }
    ], [canUpdate]);

    const actionList = useMemo(() => [
        {
            title: "View",
            handler: (row) => handleView(row)
        },
        canUpdate && {
            title: "Edit",
            handler: (row) => handleEdit(row)
        },
        canUpdate && {
            title: (row) => row?.is_active ? "Deactivate" : "Activate",
            handler: (row) => {
                setStatusItem(row);
                setShowStatusModal(true);
            }
        }
    ].filter(Boolean), [canUpdate]);

    const paginationConfig = useMemo(() => ({
        currentPage,
        totalPages: pagination?.totalPages || 1,
        total: pagination?.total || 0,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: limit.value,
        setPageSize: (size) => setLimit({ value: size, label: size }),
        setCurrentPage
    }), [currentPage, pagination, limit]);

    return (
        <div className="bg-white rounded-2 overflow-hidden common-shadow">
            <div className="p-4 d-flex align-items-center justify-content-between border-bottom gap-3">
                <div className="d-flex gap-3">

                    <TableSearch value={searchTerm}
                        placeholder="Search categories..."
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }} />
                    <CustomSelect
                        style={{ width: "200px" }}
                        label={{ isHidden: true }}
                        isCreatable={true}
                        options={config?.types || []}
                        value={selectedType}
                        onChange={(opt) => {
                            setSelectedType(opt);
                            setCurrentPage(1);
                        }}
                        isClearable={false}
                    />
                </div>

                {canCreate && (
                    <CommonButton
                        icon={<PlusIcon />}
                        variant="primary"
                        title="Add Category"
                        onClick={() => {
                            setEditItem(null);
                            setIsView(false);
                            setShowModal(true);
                        }}
                    />
                )}

            </div>

            <CustomTable
                data={list || []}
                columns={columns}
                loading={isLoading}
                emptyMessage="No categories found"
                keyField="id"
                actionList={actionList}
                pagination={paginationConfig}
            />

            <CategoryModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditItem(null);
                    setIsView(false);
                }}
                title={isView ? "View Category" : editItem ? "Edit Category" : "Add Category"}
                item={editItem}
                isView={isView}
                refresh={fetchData}
            />

            <ConfirmModal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setStatusItem(null);
                }}
                title={statusItem?.is_active ? "Deactivate Category" : "Activate Category"}
                message={
                    statusItem?.is_active
                        ? "Are you sure you want to deactivate this category?"
                        : "Are you sure you want to activate this category?"
                }
                onConfirm={handleConfirmStatusChange}
            />
        </div>
    );
};

export default CategoryList;
